import "@/App.css";
import { useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Toaster, toast } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import OfflineIndicator from "@/components/OfflineIndicator";
import SplashScreen from "@/components/SplashScreen";
import PageTransition from "@/components/PageTransition";
import ShortcutBiometricHandler from "@/components/ShortcutBiometricHandler";
import LandingPage from "@/pages/LandingPage";
import JobApplicationForm from "@/pages/JobApplicationForm";
import ConsignmentAgreementForm from "@/pages/ConsignmentAgreementForm";
import AuthPage from "@/pages/AuthPage";
import EmployeeDashboard from "@/pages/EmployeeDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import RemoteSessionsPage from "@/pages/RemoteSessionsPage";
import ContactPage from "@/pages/ContactPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import TaxPrepPage from "@/pages/TaxPrepPage";
import TaxPrepStepPage from "@/pages/TaxPrepStepPage";
import Issued1099sPage from "@/pages/Issued1099sPage";
import ScheduleInterviewPage from "@/pages/ScheduleInterviewPage";
import ManageInterviewPage from "@/pages/ManageInterviewPage";
import ApplicationResponsePage from "@/pages/ApplicationResponsePage";
import ApplicantTestPage from "@/pages/ApplicantTestPage";
import InterviewResponsePage from "@/pages/InterviewResponsePage";
import SubmitAvailabilityPage from "@/pages/SubmitAvailabilityPage";
import InvitedApplicationPage from "@/pages/InvitedApplicationPage";
import VideoCallsPage from "@/pages/VideoCallsPage";
import VideoCallPage from "@/pages/VideoCallPage";
import RecordingsPage from "@/pages/RecordingsPage";
import { IncomingCallBanner } from "@/components/IncomingCallBanner";
import { CallProvider } from "@/contexts/CallContext";
import FloatingCall from "@/components/FloatingCall";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { initShortcutHandler } from "@/utils/shortcutHandler";

// App version - increment this on each release to force cache clear
const APP_VERSION = "1.1.4";

// Session timeout in milliseconds (1 hour)
const SESSION_TIMEOUT = 3 * 60 * 60 * 1000;

// Check if running in native app
const isNativeApp = () => {
  return window.Capacitor?.isNativePlatform?.() || window.Capacitor?.isNative;
};

// Clear all caches on version change
const clearCachesOnVersionChange = async () => {
  const storedVersion = localStorage.getItem("app_version");
  
  if (storedVersion !== APP_VERSION) {
    console.log(`[App] Version changed from ${storedVersion} to ${APP_VERSION}, clearing caches...`);
    
    // Clear service worker caches
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(cache => caches.delete(cache)));
        console.log('[App] All caches cleared');
      } catch (e) {
        console.log('[App] Cache clear error:', e);
      }
    }
    
    // Tell service worker to update
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.ready;
        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
        // Force update check
        await registration.update();
        console.log('[App] Service worker update triggered');
      } catch (e) {
        console.log('[App] Service worker update error:', e);
      }
    }
    
    // Store new version
    localStorage.setItem("app_version", APP_VERSION);
    
    // Reload to get fresh content (only if we're not already in a reload loop)
    const reloadCount = parseInt(sessionStorage.getItem("reload_count") || "0");
    if (reloadCount < 1) {
      sessionStorage.setItem("reload_count", (reloadCount + 1).toString());
      window.location.reload(true);
    } else {
      sessionStorage.removeItem("reload_count");
    }
  }
};

// Run cache clear on app start
clearCachesOnVersionChange();

// Session management hook
const useSessionManager = () => {
  const clearSession = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("sessionStart");
  }, []);

  useEffect(() => {
    // Set session start time on login
    const token = localStorage.getItem("token");
    if (token && !localStorage.getItem("sessionStart")) {
      localStorage.setItem("sessionStart", Date.now().toString());
    }

    // Check session timeout
    const checkTimeout = () => {
      const sessionStart = localStorage.getItem("sessionStart");
      const token = localStorage.getItem("token");
      
      if (token && sessionStart) {
        const elapsed = Date.now() - parseInt(sessionStart);
        if (elapsed > SESSION_TIMEOUT) {
          console.log("Session expired after", SESSION_TIMEOUT / 60000, "minutes of inactivity");
          clearSession();
          window.location.href = "/login";
        }
      }
    };

    // Check timeout every minute
    const timeoutInterval = setInterval(checkTimeout, 60000);
    checkTimeout(); // Check immediately

    // NOTE: We no longer clear session on visibility change or app backgrounding
    // This was causing users to be logged out just from checking the lock screen
    // The session timeout (15 min) handles security instead

    return () => {
      clearInterval(timeoutInterval);
    };
  }, [clearSession]);

  // Reset session timer on user activity
  const resetSession = useCallback(() => {
    const token = localStorage.getItem("token");
    if (token) {
      localStorage.setItem("sessionStart", Date.now().toString());
    }
  }, []);

  return { clearSession, resetSession };
};

// Register service worker for PWA functionality
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js')
      .then((registration) => {
        console.log('ServiceWorker registered:', registration.scope);
        
        // Check for updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New content available, refresh recommended
              console.log('New content available, refresh for updates');
            }
          });
        });
      })
      .catch((error) => {
        console.log('ServiceWorker registration failed:', error);
      });
  });
}

// Redirect logged-in users past the landing page
function AuthRedirect({ children }) {
  const token = localStorage.getItem("token");
  const user = (() => { try { return JSON.parse(localStorage.getItem("user") || "{}"); } catch { return {}; } })();
  if (token && user.role) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  }
  return children;
}

// Animated Routes component
function AnimatedRoutes() {
  const location = useLocation();
  
  return (
    <ErrorBoundary>
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={
            <PageTransition>
              <LandingPage />
            </PageTransition>
          } />
        <Route path="/job-application" element={
          <PageTransition>
            <JobApplicationForm />
          </PageTransition>
        } />
        <Route path="/apply" element={
          <PageTransition>
            <JobApplicationForm />
          </PageTransition>
        } />
        <Route path="/consignment-agreement" element={
          <PageTransition>
            <ConsignmentAgreementForm />
          </PageTransition>
        } />
        <Route path="/login" element={
          <PageTransition>
            <AuthPage />
          </PageTransition>
        } />
        <Route path="/dashboard" element={
          <PageTransition>
            <EmployeeDashboard />
          </PageTransition>
        } />
        <Route path="/admin" element={
          <PageTransition>
            <AdminDashboard />
          </PageTransition>
        } />
        <Route path="/remote-sessions" element={
          <PageTransition>
            <RemoteSessionsPage />
          </PageTransition>
        } />
        <Route path="/contact" element={
          <PageTransition>
            <ContactPage />
          </PageTransition>
        } />
        <Route path="/privacy-policy" element={
          <PageTransition>
            <PrivacyPolicyPage />
          </PageTransition>
        } />
        <Route path="/reset-password/:token" element={
          <PageTransition>
            <ResetPasswordPage />
          </PageTransition>
        } />
        <Route path="/admin/tax-prep" element={
          <PageTransition>
            <TaxPrepPage />
          </PageTransition>
        } />
        <Route path="/admin/tax-prep/step/:step" element={
          <PageTransition>
            <TaxPrepStepPage />
          </PageTransition>
        } />
        <Route path="/admin/tax-prep/issued-1099s" element={
          <PageTransition>
            <Issued1099sPage />
          </PageTransition>
        } />
        <Route path="/admin/issued-1099s" element={
          <PageTransition>
            <Issued1099sPage />
          </PageTransition>
        } />
        <Route path="/schedule-interview/:token" element={
          <PageTransition>
            <ScheduleInterviewPage />
          </PageTransition>
        } />
        <Route path="/manage-interview/:cancelToken" element={
          <PageTransition>
            <ManageInterviewPage />
          </PageTransition>
        } />
        <Route path="/application-response/:token" element={
          <PageTransition>
            <ApplicationResponsePage />
          </PageTransition>
        } />
        <Route path="/applicant-test/:token" element={
          <PageTransition>
            <ApplicantTestPage />
          </PageTransition>
        } />
        <Route path="/interview-response/:token" element={
          <PageTransition>
            <InterviewResponsePage />
          </PageTransition>
        } />
        <Route path="/submit-availability/:token" element={
          <PageTransition>
            <SubmitAvailabilityPage />
          </PageTransition>
        } />
        <Route path="/apply/:token" element={
          <PageTransition>
            <InvitedApplicationPage />
          </PageTransition>
        } />
        <Route path="/video-calls" element={
          <PageTransition>
            <VideoCallsPage />
          </PageTransition>
        } />
        <Route path="/call/:roomName" element={
          <VideoCallPage />
        } />
        <Route path="/recordings" element={
          <PageTransition>
            <RecordingsPage />
          </PageTransition>
        } />
        </Routes>
      </AnimatePresence>
    </ErrorBoundary>
  );
}

function App() {
  // Check for pending shortcut - skip splash if there's one
  const [showSplash, setShowSplash] = useState(() => {
    const hasSeenSplash = sessionStorage.getItem('hasSeenSplash');
    const hasPendingShortcut = localStorage.getItem('pendingShortcutAction');
    
    // Skip splash if there's a pending shortcut action
    if (hasPendingShortcut) {
      console.log('[App] Pending shortcut detected, skipping splash');
      return false;
    }
    
    return !hasSeenSplash;
  });

  // Initialize session manager
  const { resetSession } = useSessionManager();

  // Initialize iOS Quick Actions (long-press shortcuts) handler
  // The ShortcutBiometricHandler component handles the actual Face ID + routing logic
  useEffect(() => {
    initShortcutHandler();
    console.log('[App] Shortcut handler initialized');
  }, []);

  // Reset session timer on user activity (clicks, key presses, touches)
  useEffect(() => {
    const handleActivity = () => {
      resetSession();
    };

    // Listen for user activity events
    window.addEventListener('click', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('touchstart', handleActivity);
    window.addEventListener('scroll', handleActivity);

    return () => {
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
      window.removeEventListener('scroll', handleActivity);
    };
  }, [resetSession]);

  // Request push notifications only for logged-in users (employees/consignors/admins)
  useEffect(() => {
    const requestPushNotifications = async () => {
      // Check if we're on a native platform first
      if (!window.Capacitor?.isNativePlatform?.()) {
        console.log('[Push] Not on native platform, skipping');
        return;
      }
      
      try {
        // Only request notifications if user is logged in
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const consignorEmail = localStorage.getItem('consignorEmail');
        
        // Skip if no user is logged in (casual visitors don't need notifications)
        if (!user.id && !consignorEmail) {
          console.log('[Push] Skipping - no logged in user');
          return;
        }
        
        // Determine user type and ID
        const userId = user.id || consignorEmail;
        const userType = user.role === 'admin' ? 'admin' : (user.id ? 'employee' : 'consignor');
        
        console.log(`[Push] Starting registration for ${userType}: ${userId}`);
        
        // Dynamic import to avoid issues on web
        const { PushNotifications } = await import('@capacitor/push-notifications');
        const axios = (await import('axios')).default;
        const API = process.env.REACT_APP_BACKEND_URL;
        
        // Helper function to register token with backend
        const registerTokenWithBackend = async (token) => {
          if (!token) {
            console.log('[Push] No token to register');
            return false;
          }
          
          console.log(`[Push] Registering token with backend: ${token.substring(0, 20)}...`);
          
          try {
            // Use the typed endpoint for proper user type tracking
            const response = await axios.post(`${API}/api/live-activity/register-device-token-typed`, {
              user_id: userId,
              device_token: token,
              user_type: userType
            });
            console.log('[Push] Token registered successfully:', response.data);
            localStorage.setItem('pushTokenRegistered', 'true');
            localStorage.setItem('pushTokenRegisteredAt', new Date().toISOString());
            return true;
          } catch (err) {
            console.error('[Push] Failed to register token with backend:', err.response?.data || err.message);
            return false;
          }
        };
        
        // Check permissions first
        const permStatus = await PushNotifications.checkPermissions();
        console.log('[Push] Current permission status:', permStatus);
        
        if (permStatus.receive === 'denied') {
          console.log('[Push] Permission denied - user must enable in Settings');
          return;
        }
        
        // Request permissions if not granted
        if (permStatus.receive !== 'granted') {
          console.log('[Push] Requesting permissions...');
          const permResult = await PushNotifications.requestPermissions();
          console.log('[Push] Permission result:', permResult);
          
          if (permResult.receive !== 'granted') {
            console.log('[Push] Permission denied by user');
            return;
          }
        }
        
        // Remove any existing listeners to avoid duplicates
        await PushNotifications.removeAllListeners();
        
        // Set up registration listener BEFORE calling register()
        const registrationPromise = new Promise((resolve) => {
          const timeout = setTimeout(() => {
            console.log('[Push] Registration timeout - checking localStorage for cached token');
            const cachedToken = localStorage.getItem('devicePushToken');
            if (cachedToken) {
              console.log('[Push] Found cached token, using it');
              resolve(cachedToken);
            } else {
              console.log('[Push] No cached token available');
              resolve(null);
            }
          }, 10000); // 10 second timeout
          
          PushNotifications.addListener('registration', (tokenData) => {
            clearTimeout(timeout);
            console.log('[Push] Registration event received!');
            console.log('[Push] Token value:', tokenData.value ? `${tokenData.value.substring(0, 30)}...` : 'EMPTY');
            
            if (tokenData.value) {
              // Cache the token for future use
              localStorage.setItem('devicePushToken', tokenData.value);
              resolve(tokenData.value);
            } else {
              resolve(null);
            }
          });
          
          PushNotifications.addListener('registrationError', (error) => {
            clearTimeout(timeout);
            console.error('[Push] Registration ERROR:', error);
            resolve(null);
          });
        });
        
        // Call register() to trigger the registration event
        console.log('[Push] Calling PushNotifications.register()...');
        await PushNotifications.register();
        console.log('[Push] register() completed, waiting for token...');
        
        // Wait for the token
        const token = await registrationPromise;
        
        if (token) {
          // Register with backend
          const success = await registerTokenWithBackend(token);
          if (success) {
            console.log('[Push] Push notification setup complete!');
          }
        } else {
          // Last resort: try cached token
          const cachedToken = localStorage.getItem('devicePushToken');
          if (cachedToken) {
            console.log('[Push] Using cached token as fallback');
            await registerTokenWithBackend(cachedToken);
          } else {
            console.log('[Push] No token available - push notifications will not work');
          }
        }
        
        // Set up notification received listener
        PushNotifications.addListener('pushNotificationReceived', (notification) => {
          console.log('[Push] Notification received:', notification);
        });
        
        PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
          console.log('[Push] Notification action performed:', notification);
        });
        
      } catch (error) {
        console.error('[Push] Setup error:', error.message || error);
      }
    };

    // Wait for app to fully load, then request permissions (only if logged in)
    const timer = setTimeout(requestPushNotifications, 2000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div 
      className="app-background"
      style={{ 
        background: 'linear-gradient(135deg, #1A1A2E 0%, #16213E 50%, #0F3460 100%)',
        backgroundColor: '#1A1A2E',
        minHeight: '100vh'
      }}
    >
      <BrowserRouter>
        <CallProvider>
          {showSplash && <SplashScreen onComplete={() => setShowSplash(false)} />}
          <ShortcutBiometricHandler />
          <IncomingCallBanner />
          <FloatingCall />
          <AnimatedRoutes />
        </CallProvider>
      </BrowserRouter>
      <Toaster 
        position="top-center" 
        richColors 
        toastOptions={{
          style: {
            marginTop: 'env(safe-area-inset-top, 50px)',
          },
        }}
        style={{
          top: 'env(safe-area-inset-top, 50px)',
        }}
      />
      <OfflineIndicator />
    </div>
  );
}

export default App;
